<template>
  <b-modal
    title="Schedule Meeting"
    hide-header
    hide-footer
    centered
    :visible="show"
    @hide="$emit('dismiss')"
  >
    <div v-if="user">
      <div class="profile">
        <img
          class="img-fluid img-profile rounded-circle mb-16"
          :src="user.avatar"
          :alt="`${user.first_name} ${user.last_name}`"
        />

        <div class="display-4 mb-8">
          {{ user.first_name }} {{ user.last_name }}
        </div>

        <p>{{ user.bio }}</p>

        <ul class="list-inline">
          <li
            class="list-inline-item"
            v-for="expertise in user.expertise.filter((el) => el)"
            :key="expertise.skill_id"
          >
            <b-badge variant="light">
              {{ expertise.title.split(' ')[0] }}
            </b-badge>
          </li>
        </ul>

        <div v-if="user.experience">
          <b-badge variant="highlight">
            {{
              experience[user.experience]
                ? experience[user.experience]
                : user.experience
            }}
          </b-badge>
        </div>
      </div>

      <hr />

      <b-form class="text-left" @submit.prevent="submit">
        <b-form-group label="Choose Meeting Time">
          <b-form-select
            name="time"
            v-model="time"
            :disabled="loading || calendar.length === 0"
            @change="location = calendar[time].meeting_type"
          >
            <option :value="idx" v-for="(resv, idx) in calendar" :key="idx">
              {{ resv.date }}, {{ formatTime(resv.start_time) }} to
              {{ formatTime(resv.end_time) }}
            </option>
          </b-form-select>
        </b-form-group>

        <b-form-group label="Reason for the Meeting">
          <b-form-textarea
            name="message"
            v-model="message"
            :disabled="loading || calendar.length === 0"
            rows="2"
            max-rows="2"
            placeholder="Enter something..."
          />
        </b-form-group>

        <!-- <b-form-group label="Meeting Location Preference">
          <b-form-radio-group
            name="location" v-model="location"
            :disabled="loading || calendar.length === 0"
          >
            <b-form-radio value="in-person">In-Person</b-form-radio>
            <b-form-radio value="remote">Remote</b-form-radio>
            <b-form-radio value="either">No Preference</b-form-radio>
          </b-form-radio-group>
        </b-form-group> -->

        <b-form-group>
          <label>
            <strong>Location</strong>&nbsp;
            <em class="text-muted">
              <span v-if="location == 'in-person'">(In-Person)</span>
              <span v-if="location == 'remote'">(Remote)</span>
            </em>
          </label>

          <div v-if="location == 'in-person'">
            <p style="white-space: pre-line;">{{ user.in_person_location }}</p>
            <em v-if="!user.in_person_location" class="text-muted">
              No location specified
            </em>
          </div>

          <div v-if="location == 'remote'">
            <p style="white-space: pre-line;">{{ user.remote_location }}</p>
            <em v-if="!user.remote_location" class="text-muted">
              No location specified
            </em>
          </div>
        </b-form-group>

        <hr />

        <b-btn
          class="mb-3"
          variant="secondary"
          v-if="
            $auth.user().user_type === 'advisor' &&
              user.user_type === 'entrepreneur' && 
              selectedResource == null
          "
          @click="showResources = true"
        >
          Attach Resource
        </b-btn>
        <educational-item-summary v-if="selectedResource != null" :resource="selectedResource" actionTitle="Remove" v-on:action-button-tapped="removeResource()"></educational-item-summary>

        <b-btn
          type="submit"
          variant="primary"
          block
          :disabled="loading || calendar.length === 0"
        >
          <span v-if="!loading">Book Meeting</span>
          <b-spinner v-if="loading" small></b-spinner>
        </b-btn>
      </b-form>

      <educational-item-selection-modal v-on:resource-selected="resourceSelected($event)" :show="showResources" @dismiss="showResources = false"></educational-item-selection-modal>
    </div>
  </b-modal>
</template>

<script>
import EducationalItemSelectionModal from '../educational_resources/EducationalItemSelectionModal.vue'
import EducationalItemSummary from '../educational_resources/EducationalItemSummary'

export default {
  components: { EducationalItemSelectionModal, EducationalItemSummary },
  name: 'ScheduleModal',
  props: {
    show: Boolean,
    user: Object,
  },
  data() {
    return {
      loading: false,
      calendar: [],
      time: null,
      message: null,
      location: null,
      areas: {
        finance: 'Finance',
        legal: 'Legal',
        hr: 'HR',
        strategy: 'Strategy',
        ops: 'Ops',
        tech: 'Tech',
        sales: 'Sales',
      },
      experience: {
        lt_1: '< 1 year',
        '1_5': '1-5 years',
        '5_10': '5-10 years',
        '10_20': '10-20 years',
        '20p': '20+ years',
      },
      showResources: false,
      selectedResource: null,
    }
  },
  watch: {
    show(value) {
      this.time = null
      this.message = null
      this.location = null

      if (value) {
        this.load()
      }
    },
  },
  methods: {
    load() {
      this.loading = true
      this.$http
        .get(`users/${this.user.uuid}/calendar`)
        .then(
          (res) => {
            this.calendar = []

            for (let date in res.data) {
              for (let resv in res.data[date].calendar) {
                this.calendar.push({
                  date: res.data[date].date,
                  start_time: res.data[date].calendar[resv].start_time,
                  end_time: res.data[date].calendar[resv].end_time,
                  meeting_type: res.data[date].calendar[resv].meeting_type,
                })
              }
            }

            if (this.calendar[0]) {
              this.time = 0
              this.location = this.calendar[0].meeting_type
            }
          },
          () => {
            this.$router.push('/')
          }
        )
        .finally(() => {
          this.loading = false
        })
    },
    submit() {
      this.loading = true

      const start = this.formatTime(this.calendar[this.time].start_time)
      const end = this.formatTime(this.calendar[this.time].end_time)
      let resourceId = null
      if (this.selectedResource) {
        resourceId = this.selectedResource.id.toString()
      }

      this.$http
        .post(`users/${this.user.uuid}/schedule`, {
          date: this.calendar[this.time].date,
          start_time: start,
          end_time: end,
          message: this.message,
          meeting_type: this.location,
          item_id: resourceId
        })
        .then(
          (res) => {
            this.$router.push(`/meeting/${res.data.data.uuid}`)
          },
          () => {
            alert('Unable to add meeting! Please refresh and try again.')
          }
        )
        .finally(() => {
          this.loading = false
        })
    },
    formatTime(hour) {
      let hr = hour % 12
      hr = hr === 0 ? 12 : hr

      return `${hr}:00 ${hour < 12 || hour == 24 ? 'AM' : 'PM'}`
    },
    resourceSelected(resource) {
      this.selectedResource = resource
      this.showResources = false
    },
    // When the action button on the included resource is tapped, we remove it
    removeResource() {
      this.selectedResource = null
    }
  },
}
</script>

<style scoped>
.profile {
  text-align: center;
  font-size: 0.875rem;
}

.img-profile {
  width: 96px;
  height: 96px;
}
</style>
